import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DOMPurify from "dompurify";
import Header from "../../components/Header";
import BackButton from "../../components/BackButton";
import UploadButton from "../../components/UploadButton";
import { useLocation, useNavigate } from "react-router";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";
import { ProgramFormValues } from "../../types/program";
import { uploadFile } from "../../services/exerciseService";
import {
  updateWorkoutCategory,
  updateWorkoutProgram,
} from "../../services/programService";

const ProgramValidationSchema = Yup.object().shape({
  title: Yup.string()
    .required("Name is required")
    .matches(/^(?!\s*$).+/, "Name cannot be empty or just spaces"),
  description: Yup.string()
    .required("Description is required.")
    .test(
      "not-empty-html",
      "Description cannot be empty or just spaces",
      (value) => {
        if (!value) return false;
        const sanitized = DOMPurify.sanitize(value)
          .replace(/<[^>]*>/g, "")
          .trim();
        return sanitized.length > 0;
      }
    )
    .test(
      "max-length",
      "Description cannot exceed 1000 characters.",
      (value) => {
        if (!value) return false;
        const sanitized = DOMPurify.sanitize(value, {
          ALLOWED_TAGS: [],
        }).trim();
        return sanitized.length <= 1000;
      }
    )
    .max(1007, "Description cannot exceed 1000 characters."),
  imageUrl: Yup.string().required("Image is required"),
});

const EditProgram: React.FC = () => {
  const navigate = useNavigate();
  const [, setImagePreview] = useState<string | null>(null);
  const [, setCharCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const location = useLocation();
  const program = location.state as ProgramFormValues | null;

  console.log("Program", program);

  const handleImageUpload = async (
    file: File | null,
    setFieldValue: (field: string, value: any) => void
  ): Promise<string | null> => {
    if (!file) {
      setError("Please select a valid file.");
      return null;
    }

    try {
      const uploadedImage = await uploadFile(file);

      if (uploadedImage?.url) {
        setFieldValue("imageUrl", uploadedImage.url);
        setImagePreview(uploadedImage.url);
        setError(null);
        return uploadedImage.url;
      } else {
        setError("Failed to upload the image. Please try again.");
        return null;
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setError("An error occurred during image upload.");
      return null;
    }
  };

  const handleRemoveImage = (
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue("imageUrl", null);
    setImagePreview(null);
  };

  const initialValues: ProgramFormValues = {
    id: program?.id || 0,
    type: program?.type || "",
    title: program?.title || "",
    imageUrl: program?.imageUrl || "",
    imageUrlRectangle: program?.imageUrlRectangle || "",
    description: program?.description || "",
  };

  console.log("Initial values", initialValues);

  const handleSubmit = async (values: ProgramFormValues) => {
    console.log("Program updated successfully!");
    try {
      const payload = {
        title: values.title,
        description: values.description,
        imageUrl: values.imageUrl || "",
        imageUrlRectangle: values.imageUrlRectangle || "",
      };

      if (values.type === "Program") {
        await updateWorkoutProgram(program?.id as number, payload);
      } else if (values.type === "Category") {
        await updateWorkoutCategory(program?.id as number, payload);
        payload.imageUrlRectangle = values.imageUrlRectangle || "";
      }

      setSuccessMessage("Program updated successfully!");
      setTimeout(() => {
        navigate("/programs");
      }, 2000);
    } catch (err) {
      console.log("Error updating program:", err);

      setError(err instanceof Error ? err.message : "Failed to update program");
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen flex flex-col">
      <Header title="Edit Program" />
      <div className="w-full max-w-lg bg-white shadow-md rounded-md p-6">
        {error && (
          <ErrorMessage message={error} onClose={() => setError(null)} />
        )}

        {successMessage && (
          <SuccessMessage
            message={successMessage}
            onClose={() => setSuccessMessage(null)}
          />
        )}

        <div className="flex items-center justify-between mt-4 border-b pb-4">
          <BackButton onClick={() => navigate("/programs")} />
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={ProgramValidationSchema}
          onSubmit={(values, { setSubmitting }) => {
            console.log("Submitting values:", values);
            console.log("Formik Errors:", error);
            handleSubmit(values);
            setSubmitting(false);
          }}
        >
          {({ errors, touched, setFieldValue, values }) => {
            console.log("errors", errors);
            return (
              <Form>
                <div className="mb-4">
                  <label
                    htmlFor="title"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    {values.type === "Category"
                      ? "Workout Category Name"
                      : "Program Name"}
                  </label>
                  <Field
                    id="title"
                    name="title"
                    type="text"
                    maxLength={100}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                    placeholder={`Enter ${values.type.toLowerCase()} name`}
                  />
                  {errors.title && touched.title && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.title}
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="description"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Description
                  </label>
                  <ReactQuill
                    value={values.description}
                    onChange={(content) => {
                      setFieldValue("description", content);
                      const textOnly = DOMPurify.sanitize(content, {
                        ALLOWED_TAGS: [],
                      }).trim();
                      setCharCount(textOnly.length);
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm"
                  />
                  {errors.description && touched.description && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.description}
                    </div>
                  )}
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 font-medium mb-2">
                    Upload Image
                  </label>
                  <div className="flex flex-row justify-evenly">
                    <div className="w-26 h-36 flex flex-col justify-center items-center">
                      {values.type === "Category" && <label>Image</label>}
                      <UploadButton
                        label="Upload image"
                        onUpload={(file) =>
                          handleImageUpload(file, setFieldValue)
                        }
                        onRemove={() => handleRemoveImage(setFieldValue)}
                        accept="image/*"
                        index={0}
                      />
                      {errors.imageUrl && touched.imageUrl && (
                        <div className="text-red-500 text-xs mt-1">
                          {errors.imageUrl}
                        </div>
                      )}
                    </div>
                    {values.type === "Category" && (
                      <div className="w-26 h-36 flex flex-col justify-center items-center">
                        <label>Rectangle Image</label>
                        <UploadButton
                          label="Upload image"
                          onUpload={(file) =>
                            handleImageUpload(
                              file,
                              (field: string, value: any) =>
                                setFieldValue("imageUrlRectangle", value)
                            )
                          }
                          onRemove={() =>
                            setFieldValue("imageUrlRectangle", null)
                          }
                          accept="image/*"
                          index={0}
                        />
                        {errors.imageUrlRectangle &&
                          touched.imageUrlRectangle && (
                            <div className="text-red-500 text-xs mt-1">
                              {errors.imageUrlRectangle}
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-6">
                  <label
                    htmlFor="type"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Current Images
                  </label>
                  <div className="flex flex-row justify-evenly">
                    <div className="flex flex-col justify-center items-center">
                      <label>Current Square Image</label>
                      <img
                        src={program?.imageUrl || ""}
                        alt="Program"
                        className="w-24 h-24 object-cover rounded-md"
                      />
                    </div>
                    {values.type === "Category" && (
                      <div className="flex flex-col justify-center items-center">
                        <label>Current Rectangle Image</label>
                        <img
                          src={program?.imageUrlRectangle || ""}
                          alt="Program"
                          className="w-24 h-24 object-cover rounded-md"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  onClick={() => console.log("deneme")}
                  className="w-full py-2 bg-yellow-600 text-white font-semibold rounded-md hover:bg-yellow-700 transition"
                >
                  Save Changes
                </button>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default EditProgram;
